import React, { useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"
import InputField from "@components/nonDLSComponents/InputField/InputField.js"
import { getAssetPath, Validate } from "@commonUtils"
import css from "../Auth.scss"
import { directAxiosInstance } from "@lib/api"
import { excludeCorporateRedirection } from "@commonUtils"
import { updateAuthenticationInfo, setConflictCaseInfo, setDefault } from "../reducer"
import {
    onEnterOtpGA,
    onEnterEmailOtpGA,
    changeNumberGA,
    changeEmailGA,
    resendOtpOnCallGA,
    resendOtpOnSmsGA,
    resendOtpOnEmailGA,
} from "../GAEvents"
import Button from "@components/Button/Button"
import { useDispatch, useSelector } from "react-redux"
import { setDefaultAuthConfig } from "@containers/App/reducer"
import ARIAButton from "@wrappers/ARIAButton/ARIAButton"
const SignInOtp = ({ backHandler, onSuccess, forwardHandler, state, localDispatch }) => {
    const { info, conflictInfo, authType } = state
    const { authConfig } = useSelector((state) => state.shellReducer)
    const [timeLeft, setTimeLeft] = useState(30)
    const [error, setError] = useState(null)
    const [alertType, setAlertType] = useState(error)
    const dispatch = useDispatch()
    // we are using "active" as a flag so that untill the request is completed, we do not allow multiple request
    // on multiple clicks on cta
    const [active, setActive] = useState(false)
    const [otp, setOtp] = useState("")
    const [ctaLabel, setCtaLabel] = useState("Done")
    const [iconUrl, setIconUrl] = useState(null)
    const icon = getAssetPath("svgs/icons/loading-wheel_24px_background.svg")
    useEffect(() => {
        if (timeLeft > 0) {
            setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
        }
    }, [timeLeft])
    const [focused, setFocused] = useState(false)
    const onFocus = () => {
        setFocused(true)
    }

    const onBlur = () => {
        if (otp === "") {
            setFocused(false)
        }
    }

    const inputRef = useRef(null)
    const setInputFocus = () => {
        setFocused(true)
        inputRef.current.focus()
    }

    /**
     * @description when user come on this screen cursor should be on inputField .
     */
    useEffect(() => {
        setInputFocus()
    }, [])

    useEffect(() => {
        if (!error) return

        if (error.otp) {
            setInputFocus()
        }
    }, [error])

    /**
     *
     * @description Function to submit the form by clicking on enter
     */
    const onKeyUp = (event) => {
        if (event.key === "Enter") {
            handleSubmit()
        }
    }
    /**
     * @description Function to check valid input
     */
    const errorCheck = () => {
        setError(null)

        const errs = Validate({
            otp: otp,
        })
        setError(errs)
        if (errs) {
            setAlertType("error")
        }
        return errs
    }

    /**
     * @description Function to handle change email / phone (not applicable for merchant flow)
     */

    const onChangeClick = () => {
        info.phone ? changeNumberGA("Login") : changeEmailGA("Login-Email")
        backHandler()
    }

    /**
     * @description Function to resend otp on sms or call
     */

    const resendOtpPhone = (onCall = false) => {
        directAxiosInstance()
            .post(authType === "MerchantOtp" ? "auth/b2b/verify_token" : "auth/create_token", {
                number: info.phone,
                otp_on_call: onCall,
            })
            .then((res) => {
                if (res && res.data) {
                    if (!res.data.errors && !res.data.error) {
                        if (authType !== "MerchantOtp") {
                            onCall
                                ? resendOtpOnCallGA("Login", "Success")
                                : resendOtpOnSmsGA("Login", "Success")
                        }
                        setTimeLeft(30)

                        setError({
                            otp: `You will receive ${onCall ? "a" : "an"} ${
                                onCall ? "call" : "sms"
                            } with OTP`,
                        })
                        setTimeout(() => {
                            setError({
                                otp: null,
                            })
                        }, 30000)

                        setAlertType("success")
                    }
                }
            })
            .catch((err) => {
                if (authType !== "MerchantOtp") {
                    onCall ? resendOtpOnCallGA("Login", "Error") : resendOtpOnSmsGA("Login", "Error")
                }
                const errMsg = err?.data?.error?.message || "Server error"
                setError({ otp: errMsg })
                setAlertType("error")
            })
    }

    /**
     * @description Function to resend otp on email
     */

    const resendOtpEmail = () => {
        directAxiosInstance()
            .post("auth/create_token_email", {
                email: info.email,
            })
            .then((res) => {
                if (res && res.data) {
                    if (!res.data.errors && !res.data.error) {
                        resendOtpOnEmailGA("Verify Email id - login", "Success")
                        setTimeLeft(30)
                        setError({
                            otp: `You will receive an email with OTP`,
                        })
                        setTimeout(() => {
                            setError({
                                otp: null,
                            })
                        }, 30000)
                        setAlertType("success")
                    }
                }
            })
            .catch((err) => {
                resendOtpOnEmailGA("Verify Email id - login", "Error")
                const errMsg = err?.data?.error?.message || "Server error"
                setError({ otp: errMsg })
                setAlertType("error")
            })
    }

    /**
     * @description Function to authenticate user based on otp input. If we have the user data then the authentication flow
     *              is complete, otherwise it is part of the conflicting or merge flow
     */
    const handleSubmit = () => {
        const errs = errorCheck()

        if (!errs && !active) {
            setActive(true)
            setIconUrl(icon)

            const data = {
                verification_token: otp,
            }
            info.phone ? (data.number = info.phone) : (data.email = info.email)
            if (info.email && conflictInfo.conflict) {
                data.conflict = conflictInfo.conflict
            }
            directAxiosInstance()
                .post(info.phone ? "auth/verify_token" : "auth/verify_token_email", {
                    ...data,
                })
                .then((res) => {
                    if (res && res.data) {
                        if (!res.data.errors && !res.data.error) {
                            const userData = res?.data?.user_data
                            const userFlags = res?.data?.user_flags
                            if (userData) {
                                const corporateRedirectionUrl = excludeCorporateRedirection()
                                onSuccess(userData, corporateRedirectionUrl)
                                info.phone ? onEnterOtpGA("Login", "Success") : onEnterEmailOtpGA("Success")
                            } else {
                                if (userFlags?.email && userFlags?.user_type === "migrated") {
                                    localDispatch(updateAuthenticationInfo({ email: userFlags.email }))
                                }
                                if (userFlags?.conflict) {
                                    info.phone
                                        ? onEnterOtpGA("Login", "Error, conflicting journey")
                                        : onEnterEmailOtpGA("Error, conflicting journey")
                                    localDispatch(
                                        setConflictCaseInfo({
                                            requiredDetail: info.email ? "phone" : "email",
                                            conflict: userFlags.conflict,
                                            description: userFlags?.description,
                                            header: userFlags?.header,
                                            cta: userFlags?.cta,
                                            optionsList: userFlags?.conflict_data?.map((val) => {
                                                return {
                                                    value: val.email,
                                                    display: val.email,
                                                }
                                            }),
                                            number: userFlags?.number,
                                        })
                                    )

                                    userFlags?.conflict_data?.length > 0
                                        ? forwardHandler("ConflictEmailList")
                                        : forwardHandler("EmailConflict")
                                } else if (userFlags?.is_email_required) {
                                    userFlags?.user_type === "existing"
                                        ? onEnterOtpGA("Signup", "Success")
                                        : onEnterOtpGA("Login", "Success")
                                    forwardHandler("EmailConflict")
                                } else if (userFlags?.is_number_required) {
                                    onEnterEmailOtpGA("Error, enter mobile number")
                                    forwardHandler("PhoneConflict")
                                }
                            }
                        }
                        setActive(false)
                    }
                })
                .catch((err) => {
                    setTimeLeft(0)
                    const errMsg = err?.data?.error?.message || "Server error"
                    setError({ otp: errMsg })
                    setAlertType("error")
                    info.phone
                        ? info.existingUser
                            ? onEnterOtpGA("Login", "Error")
                            : onEnterOtpGA("Signup", "Error")
                        : onEnterEmailOtpGA("Error")
                    setIconUrl(null)
                    setCtaLabel("Done")
                    setActive(false)
                })
        }
    }

    /**
     * @description Function to authenticate user based on otp input. The below flow is for merchant flow only to authenticate
     *              their content hash data
     */
    const handleMerchantOtpSubmit = () => {
        const errs = errorCheck()

        if (!errs && !active) {
            setActive(true)
            setIconUrl(icon)
            const data = {
                verification_token: otp,
                number: info.phone,
            }
            directAxiosInstance()
                .post("auth/b2b/verify_token", data)
                .then((res) => {
                    if (res && res.data) {
                        if (!res.data.errors && !res.data.error) {
                            const userData = res?.data?.user_data
                            const userFlags = res?.data?.user_flags
                            if (userData) {
                                const corporateRedirectionUrl = excludeCorporateRedirection()
                                onSuccess(userData, corporateRedirectionUrl)
                            } else {
                                if (userFlags?.email && userFlags?.user_type === "migrated") {
                                    localDispatch(updateAuthenticationInfo({ email: userFlags.email }))
                                }
                                if (userFlags?.conflict && userFlags?.conflict_data?.length) {
                                    localDispatch(
                                        setConflictCaseInfo({
                                            requiredDetail: "email",
                                            conflict: userFlags?.conflict,
                                            description: userFlags?.description,
                                            header: userFlags?.header,
                                            cta: userFlags?.cta,
                                            optionsList: userFlags?.conflict_data?.map((val) => {
                                                return {
                                                    value: val.email,
                                                    display: val.email,
                                                }
                                            }),
                                            number: userFlags?.number,
                                        })
                                    )
                                    forwardHandler("ConflictEmailList")
                                } else if (userFlags?.is_email_required) {
                                    forwardHandler("EmailConflict")
                                }
                            }
                        }
                        setActive(false)
                    }
                })
                .catch((err) => {
                    setTimeLeft(0)
                    const errMsg = err?.data?.error?.message || "Server error"
                    setError({ otp: errMsg })
                    setIconUrl(null)
                    setCtaLabel("Done")
                    setActive(false)
                })
        }
    }

    return (
        <div className={css.rightContainer}>
            {!authConfig.restrictUserFromClosingAuth && (
                <div className={`flex ${css.crossIcon} ${css.cursorPointer}`}>
                    <div>
                        <ARIAButton
                            onClick={() => {
                                dispatch(setDefaultAuthConfig())
                                localDispatch(setDefault())
                            }}
                            ariaLabel={"Close login popup"}
                        >
                            <img src={getAssetPath("svgs/icons/cross_18px_primary.svg")} alt={"Close icon"} />
                        </ARIAButton>
                    </div>
                </div>
            )}
            <div className={`${css.authContainer}`}>
                <div className={`${css.otpContainer} flex flexColumn justifyBetween`}>
                    <div>
                        <span className={css.heading}>Verify OTP</span>
                        <div className={`smallRegular ${css.subHeadingMargin}`}>
                            Provide OTP sent to <span>{info.email || info.phone}</span>
                            {authType !== "MerchantOtp" && (
                                <span
                                    className={`textAction smallSemiBold ${css.marginLeft4}`}
                                    onClick={() => onChangeClick()}
                                >
                                    <u>Edit</u>
                                </span>
                            )}
                        </div>

                        <span className={css.marginBottom16} data-testid="inputField_otp">
                            <InputField
                                name="otp"
                                label={"One Time Password"}
                                onChange={setOtp}
                                alertMessage={error && error.otp}
                                countDown={timeLeft ? `Resend in 00:${timeLeft}` : ""}
                                focused={focused}
                                onFocus={() => onFocus()}
                                onBlur={() => onBlur()}
                                borderType={"primary"}
                                onKeyUp={(e) => onKeyUp(e)}
                                alertType={alertType}
                                ref={inputRef}
                                required
                            />
                        </span>

                        {info.phone && (
                            <div className="flex alignCenter justifyAround">
                                {!timeLeft && (
                                    <>
                                        <ConditionalLink
                                            className={`textAction bodySemiBold ${css.cursorPointer}`}
                                            onClick={() => resendOtpPhone()}
                                        >
                                            Get OTP on SMS
                                        </ConditionalLink>
                                        <div className={`${css.marginBoth12} textSecondary`}>|</div>
                                        <ConditionalLink
                                            className={`textAction bodySemiBold ${css.cursorPointer}`}
                                            onClick={() => resendOtpPhone(true)}
                                        >
                                            Get OTP on Call
                                        </ConditionalLink>
                                    </>
                                )}
                            </div>
                        )}
                        {info.email && (
                            <div className={` marginTop-16 flex ${css.rightAlign}`}>
                                {!timeLeft && (
                                    <ConditionalLink
                                        className={`textAction bodySemiBold  ${css.cursorPointer} `}
                                        onClick={() => resendOtpEmail()}
                                    >
                                        Resend OTP
                                    </ConditionalLink>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="  flexColumn justifyBetween alignCenter ">
                        <Button
                            label={!iconUrl ? ctaLabel : null}
                            iconUrl={iconUrl ? iconUrl : null}
                            isDisabled={iconUrl ? true : false}
                            onClick={() =>
                                authType === "MerchantOtp" ? handleMerchantOtpSubmit() : handleSubmit()
                            }
                        />
                        <div className="marginTop-64 flexColumn alignCenter justifyCenter">
                            <div className={css.footerSubText}>
                                <span className="flexColumn alignCenter">
                                    <ConditionalLink
                                        className={`textAction xSmallSemiBold  ${css.cursorPointer}`}
                                        onClick={() => forwardHandler("NeedHelp")}
                                    >
                                        <span>Need Help?</span>
                                        <span className={css.marginLeft4}> Get in touch</span>
                                    </ConditionalLink>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

SignInOtp.propTypes = {
    backHandler: PropTypes.func.isRequired,
    forwardHandler: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    state: PropTypes.shape({
        authType: PropTypes.string,
        info: PropTypes.shape({
            email: PropTypes.string,
            phone: PropTypes.string,
            existingUser: PropTypes.bool,
        }),
        conflictInfo: PropTypes.shape({
            conflict: PropTypes.bool,
        }),
    }).isRequired,
    localDispatch: PropTypes.func.isRequired,
}

export default SignInOtp
